import type React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { createFeedbackBodyZod } from "@nlc/shared-types";
import { Paperclip, X } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import type { CreateFeedbackFormData } from "@/features/request/feedback-tool/feedbackTool/hooks/useCreateFeedback.tsx";

import { Button } from "@/components/button";
import { toast } from "@/components/toast/toast-queue.ts";
import { FileUploaded } from "@/features/request/feedback-tool/feedbackTool/components/FileUploaded.tsx";
import { Loading } from "@/features/request/feedback-tool/feedbackTool/components/Loading.tsx";
import { useCreateFeedback } from "@/features/request/feedback-tool/feedbackTool/hooks/useCreateFeedback.tsx";
import { useFeedbackTool } from "@/features/request/feedback-tool/feedbackTool/hooks/useFeedbackTool.tsx";
import { useUploadAssets } from "@/features/request/feedback-tool/feedbackTool/hooks/useUploadAssets.tsx";
import { useGetRequest } from "@/hooks/useGetRequest.tsx";
import { useUser } from "@/hooks/useUser.tsx";

import { TextareaEditor } from "../../components/TextareaComment";
import { useLoadingMutation } from "../../hooks/useLoadingMutation";

export function Form({
  imgRef,
}: {
  imgRef: React.MutableRefObject<HTMLImageElement | null>;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const balloonRef = useRef<HTMLDivElement | null>(null);
  const { data } = useUser();
  const [formPosition, setFormPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [createError, setCreateError] = useState("");
  const [renderAssets, setRenderAssets] = useState<
    Array<{ attachmentId: number; name: string }>
  >([]);
  const { data: request } = useGetRequest();
  const requestId = request?.id;

  const {
    divImgRef,
    formFeedBack,
    img,
    setFormFeedBack: setFormFeedback,
    zoom,
  } = useFeedbackTool();

  const formMethods = useForm<CreateFeedbackFormData>({
    defaultValues: {
      assets: [],
      attachments: [],
      comment: "",
      coordinates: "",
      parentId: img.id,
      userId: data?.user.id ?? 0,
    },
    resolver: zodResolver(createFeedbackBodyZod),
  });

  const assets = formMethods.watch("assets") || [];

  const { handleFileChange, handleFileDelete } = useUploadAssets(
    "assets",
    assets,
    () => {},
    formMethods,
  );

  const isUploading = assets.some(
    (item) =>
      (item as { status: "error" | "success" | "uploading" }).status ===
      "uploading",
  );

  const isError = assets.some(
    (item) =>
      (item as { status: "error" | "success" | "uploading" }).status ===
      "error",
  );

  const { mutate } = useCreateFeedback(img.id, Number(requestId));
  const isLoading = useLoadingMutation("CREATE", img.id);

  useEffect(() => {
    const convert = (item: number) => `${item * 100}%`;

    if (formFeedBack) {
      formMethods.setValue(
        "coordinates",
        `${convert(Number(formFeedBack?.y))},${convert(Number(formFeedBack?.x))}`,
      );
    }
  }, [formFeedBack]);

  useEffect(() => {
    if (assets?.length > 0) {
      const removeErrors = assets.filter(
        (item) =>
          (item as { status: "error" | "success" | "uploading" }).status !==
          "error",
      );

      const includeAttachments = removeErrors?.map((att) => ({
        attachmentId: att.attachmentId ?? 0,
        name: (att as { file: File }).file.name,
      }));

      setRenderAssets(includeAttachments);
      formMethods.setValue("attachments", includeAttachments);

      setFormFeedback({
        attachments: includeAttachments,
        description: formFeedBack?.description,
        format: "IMAGE",
        id: 0,
        imgId: img.id,
        storyId: 0,
        type: "ADD",
        user: {
          id: 0,
          name: "",
        },
        x: formFeedBack?.x ?? 0,
        y: formFeedBack?.y ?? 0,
      });
    }
  }, [assets]);

  useEffect(() => {
    if (!imgRef.current || !formFeedBack) return;

    const right =
      imgRef.current.clientWidth -
        Number(formFeedBack?.x) * imgRef?.current?.width >
      320;
    const bottomFull =
      Number(formFeedBack?.y) * imgRef?.current?.height >
      imgRef?.current?.height - 220;

    const image = imgRef.current;

    if (!image || !divImgRef.current) return;

    setFormPosition({
      left: right
        ? imgRef.current?.offsetLeft +
          Number(formFeedBack?.x) * imgRef.current?.clientWidth
        : imgRef.current?.offsetLeft +
          Number(formFeedBack?.x) * imgRef.current?.clientWidth -
          285,
      top: bottomFull
        ? imgRef.current?.offsetTop +
          Number(formFeedBack?.y) * imgRef.current?.height -
          150
        : imgRef.current?.offsetTop +
          Number(formFeedBack?.y) * imgRef.current?.height +
          40,
    });
  }, [formFeedBack, zoom]);

  // Keep the balloon fully inside the container's visible area (it can end up
  // off-screen on narrow viewports); re-runs after its own correction with a
  // zero delta, so it settles in one pass.
  useLayoutEffect(() => {
    const balloon = balloonRef.current;
    const container = divImgRef.current;

    if (!balloon || !container || !formPosition) return;

    const margin = 8;
    const balloonRect = balloon.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    let dx = 0;
    let dy = 0;

    if (balloonRect.right > containerRect.right - margin) {
      dx = containerRect.right - margin - balloonRect.right;
    }
    if (balloonRect.left + dx < containerRect.left + margin) {
      dx = containerRect.left + margin - balloonRect.left;
    }
    if (balloonRect.bottom > containerRect.bottom - margin) {
      dy = containerRect.bottom - margin - balloonRect.bottom;
    }
    if (balloonRect.top + dy < containerRect.top + margin) {
      dy = containerRect.top + margin - balloonRect.top;
    }

    if (dx || dy) {
      setFormPosition((prev) =>
        prev ? { left: prev.left + dx, top: prev.top + dy } : prev,
      );
    }
  }, [formPosition]);

  useEffect(() => {
    if (isError) {
      toast.add(
        {
          description: "Unsupported file extension.",
          title: "Unsupported Media Type",
          type: "error",
        },
        {
          timeout: 5000,
        },
      );
    }
  }, [isError]);

  useEffect(() => resetForm(), [img.id]);

  const resetForm = () => {
    formMethods.reset({
      assets: [],
      attachments: [],
      comment: "",
      coordinates: "",
      parentId: img.id,
      userId: data?.user.id ?? 0,
    });
  };

  return (
    <>
      {formFeedBack?.format === "IMAGE" && formFeedBack?.type === "ADD" ? (
        <div
          className="absolute border-2 bg-white border-black border-dashed w-7 h-7 flex justify-center items-center rounded-full z-[2] shadow-xl"
          style={{
            left: imgRef?.current?.width
              ? imgRef?.current?.offsetLeft +
                Number(formFeedBack?.x) * imgRef?.current?.clientWidth
              : 0,
            top: imgRef?.current?.height
              ? imgRef?.current?.offsetTop +
                Number(formFeedBack?.y) * imgRef?.current?.clientHeight
              : 0,
          }}
        />
      ) : null}
      {formPosition && formFeedBack?.type === "ADD" ? (
        <div
          className="absolute bg-bg-primary w-80 max-w-[calc(100vw-2rem)] p-4 rounded-md shadow-lg z-[9999] origin-top-left"
          data-name="box-image"
          onClick={(event) => {
            event.stopPropagation();
          }}
          ref={balloonRef}
          style={{
            left: formPosition?.left,
            top: formPosition?.top,
          }}
        >
          <div className="flex items-start">
            <Controller
              control={formMethods.control}
              name="comment"
              render={({ field: { name, onChange, value } }) => (
                <TextareaEditor name={name} onChange={onChange} value={value} />
              )}
            />
            <Button
              aria-label="Scope help"
              className="h-min cursor-default"
              onPress={() => {
                setFormFeedback(null);
                setFormPosition(null);
              }}
              variant="unstyled"
            >
              <X className="text-slate-400 mb-3 cursor-pointer" size={24} />
            </Button>
          </div>
          <span className="block text-sm font-normal text-text-error-primary mt-sm">
            {createError}
          </span>
          <div>
            {renderAssets.length > 0
              ? renderAssets?.map((asset) => (
                  <FileUploaded
                    key={asset.attachmentId}
                    name={asset.name}
                    onDelete={() => handleFileDelete(asset?.attachmentId ?? 0)}
                  />
                ))
              : null}
          </div>
          <div className="flex mt-4">
            <Button
              className="bg-base"
              onPress={() => {
                inputRef.current?.click();
              }}
              variant="secondary"
            >
              {isUploading ? (
                <Loading />
              ) : (
                <>
                  <input
                    className="hidden"
                    multiple
                    onChange={handleFileChange}
                    ref={inputRef}
                    type="file"
                  />
                  <Paperclip
                    className="text-slate-400 cursor-pointer"
                    size={24}
                  />
                </>
              )}
            </Button>
            <Button
              className="w-full ml-2"
              isDisabled={!!isLoading}
              onPress={() => {
                mutate(formMethods.getValues(), {
                  onError: (err: unknown) => {
                    const comment =
                      "body/comment must NOT have fewer than 1 characters";
                    const message = err instanceof Error ? err.message : "";

                    if (message === comment) {
                      setCreateError("Message is required");

                      return toast.add(
                        {
                          description: "Message is required",
                          title: "Feedback not updated",
                          type: "warning",
                        },
                        {
                          timeout: 5000,
                        },
                      );
                    }
                    if (
                      message ===
                      "Failed to create feedback: One or more attachments are invalid"
                    ) {
                      return toast.add({
                        description: "One or more attachments are invalid",
                        title: "Feedback not updated",
                        type: "error",
                      });
                    }
                    toast.add(
                      {
                        description: message,
                        title: "Feedback not updated",
                        type: "error",
                      },
                      {
                        timeout: 5000,
                      },
                    );
                  },
                  onSuccess: () => {
                    resetForm();
                    setRenderAssets([]);
                    setFormPosition(null);
                    setFormFeedback(null);
                    setCreateError("");
                  },
                });
              }}
              type="button"
            >
              {isLoading ? <Loading /> : "Add Feedback"}
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}
